<script setup lang="ts">
import { T } from '@tolgee/vue'
import TrophyImage from '@/assets/images/emojis/emoji-trophy.png'
import ReceiptImage from '@/assets/images/receipt.png'
import LevelProgressBar from '@/components/LevelProgressBar.vue'
import { computed, ref } from 'vue'
import InfoCircleIcon from '@/components/icons/InfoCircleIcon.vue'
import LingoGiveawayLevelUpToGetEggModal
  from '@/partners/lingo/lingo-giveaway/components/modals/LingoGiveawayLevelUpToGetEggModal.vue'
import { formatDuration } from '@/helpers/date'

const props = defineProps<{
  remainedTime: number
}>()

const oneDayInSeconds = 3600 * 24
const transactionCompleted = ref<boolean>(true)
const progress = computed<number>(() => ((oneDayInSeconds - props.remainedTime) * 100) / oneDayInSeconds)
const countdown = computed(() => formatDuration(props.remainedTime))
const showLevelUpToGetEggModal = ref(false)
</script>
<template>
  <div class="flex flex-col w-full items-center gap-y-3">
    <h2 class="font-bold text-xl tracking-tight text-light-white">
      <T
        key-name="lingo.giveaway.hatch.title"
        default-value="There are 2 ways to hatch your eggs"
      />
    </h2>
    <div class="w-full flex flex-col p-4 rounded-xl bg-dark-bottom-layer">
      <div class="flex items-center gap-x-1.5">
        <img
          class="w-6 h-6 min-w-6"
          :src="ReceiptImage"
        >
        <h2
          class="text-light-white text-lg tracking-tight font-bold whitespace-break-spaces"
        >
          <T
            key-name="lingo.giveaway.hatch.transaction.title"
            default-value="1.  Make a daily transaction"
          />
        </h2>
      </div>
      <template
        v-if="transactionCompleted"
      >
        <LevelProgressBar
          :progress="progress"
          progress-bar-class="bg-dark-top-most-layer"
          class="mb-1 mt-3"
        />
        <div class="w-full flex justify-between items-center">
          <p
            v-if="progress === 100"
            class="font-medium text-base text-teal-primary"
          >
            <T
              key-name="lingo.giveaway.hatch.transaction.complete.description"
              default-value="You can now hatch a new egg!"
            />
          </p>
          <p
            v-else
            class="font-medium text-base text-white"
          >
            <T
              key-name="lingo.giveaway.hatch.transaction.incomplete.description"
              default-value="Next egg hatches in:"
            />
          </p>
          <p class="text-white text-base font-semibold tracking-tight">
            {{ countdown }}
          </p>
        </div>
      </template>
      <p
        v-else
        class="font-medium text-base text-white/60 mt-2"
      >
        <T
          key-name="lingo.giveaway.hatch.transaction.first.description"
          default-value="Complete one small transaction per day to hatch your egg and collect a Maverick."
        />
      </p>
    </div>
    <div class="w-full flex flex-col gap-y-2 p-4 rounded-xl bg-dark-bottom-layer">
      <div class="flex items-center gap-x-1.5">
        <img
          class="w-6 h-6 min-w-6"
          :src="TrophyImage"
        >
        <h2
          class="text-light-white text-lg tracking-tight font-bold whitespace-break-spaces flex items-center gap-x-2"
        >
          <T
            key-name="lingo.giveaway.hatch.level_up.title"
            default-value="2.  Stay Active in Dropee!"
          />
          <InfoCircleIcon
            class="h-5 w-5 min-w-[20px] text-white/[38%]"
            @click="showLevelUpToGetEggModal = true"
          />
        </h2>
      </div>
      <p class="font-medium text-base text-white/60">
        <T
          key-name="lingo.giveaway.hatch.level_up.description"
          :default-value="`Each time you crack an egg, you have a chance to crack an extra one. The higher your level, the higher your chance.`"
        />
      </p>
    </div>
  </div>

  <LingoGiveawayLevelUpToGetEggModal
    :show="showLevelUpToGetEggModal"
    @hide="showLevelUpToGetEggModal = false"
  />
</template>